import { inject, Injectable } from '@angular/core';
import { driver, DriveStep } from 'driver.js';
import {
  Entry,
  GhostEntry,
  TourConfigList,
  TourStaticSteps,
  TourStep,
  VisibleEntry,
} from '../../models/tour';
import { isNullOrUndefined } from '../../utils/utils';
import { Router } from '@angular/router';
import { TourStepAction } from '../../enums/tour-config';
import { AuthService } from '../../../pages/auth/services/auth.service';

@Injectable({ providedIn: 'root' })
export class TourService {
  router = inject(Router);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private driverObj: any;
  private tourSteps: TourStep[] = [];
  isTourMode = false;
  authService = inject(AuthService);

  getTourStepsForUrl(fullUrl: string): TourStep[] {
    // eslint-disable-next-line prefer-const
    let [basePath, queryString] = fullUrl.split('?');

    if (basePath.startsWith('/cdfi/profile/')) {
      basePath = '/cdfi/profile';
    }

    const routeConfig = TourConfigList[basePath];

    if (!routeConfig) return [];

    if (routeConfig.matchers?.length) {
      const searchParams = new URLSearchParams(queryString ?? '');
      const params: Record<string, string> = {};
      searchParams.forEach((value, key) => {
        params[key] = value;
      });

      const matched = routeConfig.matchers.find(({ match }) => match(params));
      if (matched) return matched.steps;
    }

    if (basePath === '/dashboard') {
      const isSmeUser = this.authService.isSMEUser();
      return isSmeUser
        ? routeConfig?.optionalSteps || []
        : routeConfig.defaultSteps;
    }

    return routeConfig.defaultSteps;
  }

  updateTourSteps(url: string): void {
    const resolved = this.getTourStepsForUrl(url);
    this.tourSteps =
      resolved.length > 0
        ? [
            ...TourStaticSteps['header'],
            ...TourStaticSteps['sidebarMenu'],
            ...resolved,
          ]
        : [];
  }

  onHelp(): void {
    this.isTourMode = true;
    const routeSteps = this.buildDriveSteps(
      this.getTourStepsForUrl(this.router.url),
    );
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => this.runTour(routeSteps), 200);
  }

  startTour(): void {
    this.isTourMode = true;
    const driveSteps = this.buildDriveSteps(this.tourSteps);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      this.runTour(driveSteps);
    }, 200);
  }

  private buildDriveSteps(steps: TourStep[]): DriveStep[] {
    // Separate steps into two categories:
    // 1. "visible" steps: element exists in DOM → shown normally
    // 2. "action-only" steps: element NOT in DOM but has an action → fire action silently, skip rendering
    //    (steps with no element and no action are dropped entirely)

    const isElementInDom = (step: TourStep): boolean => {
      if (step.requireDynamicDOM) return true;
      const config = step.driverConfig;
      if (typeof config?.element === 'string') {
        const elementId = config.element.slice(1);
        return !!document.getElementById(elementId);
      }

      return false;
    };

    const hasAction = (step: TourStep): boolean =>
      !!step.action && !!step.actionTargetId;

    const executeAction = (step: TourStep): void => {
      if (!step.actionTargetId) return;

      const target = document.getElementById(step.actionTargetId);

      if (!target) return;

      switch (step.action) {
        case TourStepAction.Click:
          target.click();
          break;

        case TourStepAction.Checkbox:
          if (
            target instanceof HTMLInputElement &&
            target.type === 'checkbox'
          ) {
            target.checked = !target.checked;
            target.dispatchEvent(new Event('change', { bubbles: true }));
          }
          break;
      }
    };

    const entries: Entry[] = steps
      .map((step): Entry | null => {
        if (isElementInDom(step)) {
          return { kind: 'visible', step };
        }
        if (hasAction(step)) {
          // Not in DOM, but has an action — treat as ghost
          return { kind: 'ghost', step };
        }
        return null;
      })
      .filter((e): e is Entry => e !== null);

    const visibleEntries = entries.filter(
      (e): e is VisibleEntry => e.kind === 'visible',
    );

    const handleAction = (step: TourStep): void => executeAction(step);

    const ghostsBefore = (visibleIndex: number): TourStep[] => {
      let i = visibleIndex + 1;
      const ghosts: TourStep[] = [];
      while (i < entries.length && entries[i].kind === 'ghost') {
        ghosts.push((entries[i] as GhostEntry).step);
        i++;
      }
      return ghosts;
    };

    return visibleEntries.map((entry, visibleIdx) => {
      const step = entry.step;
      const cfg = { ...step.driverConfig };
      cfg.popover = { ...cfg.popover };

      let hasPermission = false;

      if (step.permissions && step.permissions.length > 0) {
        if (step.permissionMatchType === 'ALL') {
          hasPermission =
            step.permissions.every((permission) =>
              this.authService.hasPermission(permission),
            ) ?? false;
        } else {
          hasPermission =
            step.permissions?.some((permission) =>
              this.authService.hasPermission(permission),
            ) ?? false;
        }
      }

      if (hasPermission) {
        cfg.popover.description =
          step?.descriptionWithPermission ?? cfg.popover.description;
        cfg.popover.title = step?.titleWithPermission ?? cfg.popover.title;
      }

      const pendingGhosts = ghostsBefore(entries.findIndex((e) => e === entry));

      const hasVisibleAction =
        step.action === TourStepAction.Click ||
        step.action === TourStepAction.Checkbox;

      if (hasVisibleAction || pendingGhosts.length > 0) {
        cfg.popover.onNextClick = () => {
          const nextBtn = document.querySelector(
            '.driver-popover-next-btn',
          ) as HTMLButtonElement;

          if (nextBtn) {
            nextBtn.disabled = true;
            nextBtn.innerText = 'Loading...';
            nextBtn.style.cursor = 'not-allowed';
          }

          if (hasVisibleAction) {
            handleAction(step);
          }

          pendingGhosts.forEach((ghostStep) => executeAction(ghostStep));

          const nextVisibleStep = visibleEntries[visibleIdx + 1];
          const nextSelector = nextVisibleStep?.step?.driverConfig
            ?.element as string;

          const restoreBtn = () => {
            if (nextBtn) {
              nextBtn.disabled = false;
              nextBtn.innerText = 'Next';
              nextBtn.style.cursor = 'pointer';
            }
          };

          if (nextSelector) {
            this.waitForElement(
              nextSelector,
              () => {
                restoreBtn();
                this.driverObj.moveNext();
                this.driverObj.refresh();
              },
              () => {
                restoreBtn();
                this.driverObj.moveNext();
              },
            );
          } else {
            restoreBtn();
            this.driverObj.moveNext();
          }
        };
      }

      return cfg;
    });
  }

  private runTour(steps: DriveStep[]): void {
    const disableScroll = () => {
      document.body.style.overflow = 'hidden';
    };

    const enableScroll = () => {
      document.body.style.overflow = '';
    };

    this.driverObj = driver({
      steps,
      popoverClass: 'driverjs-theme',
      nextBtnText: 'Next',
      showButtons: ['next', 'close'],
      onPopoverRender: (popover) => {
        const currentIndex = this.driverObj.getActiveIndex?.() ?? 0;

        const restartBtn = document.createElement('button');
        restartBtn.innerText = 'Restart';
        restartBtn.id = 'restartBtn';
        popover.footerButtons.prepend(restartBtn);
        restartBtn.style.display = currentIndex === 0 ? 'none' : 'block';

        restartBtn.addEventListener('click', () => {
          const el = document.getElementById('secondary-popup-close');

          if (el) {
            el.click();
          }

          const achModalClose = document.getElementById(
            'tour-achievement-modal-close',
          );
          if (achModalClose) {
            achModalClose.click();
          }

          this.driverObj.drive(0);
        });
      },

      onHighlightStarted: (element, step, { driver }) => {
        if (isNullOrUndefined(element)) {
          setTimeout(() => driver.moveNext(), 0);
          return;
        }

        this.waitForElement(step.element as string, () => {
          driver.refresh();
          setTimeout(() => driver.refresh(), 300);
        });
      },

      onDestroyed: () => {
        enableScroll();
        this.isTourMode = false;
        this.authService.updateUserOnboard();

        const secondaryClose = document.getElementById('secondary-popup-close');
        if (secondaryClose) {
          secondaryClose.click();
        }

        const achModalClose = document.getElementById(
          'tour-achievement-modal-close',
        );
        if (achModalClose) {
          achModalClose.click();
        }
      },
    });

    disableScroll();
    this.driverObj.drive();
  }

  private waitForElement(
    selector: string,
    onFound: () => void,
    onNotFound?: () => void,
    maxAttempts = 20,
  ): void {
    if (!selector) {
      onFound();
      return;
    }

    const id = selector.startsWith('#') ? selector.slice(1) : selector;

    let attempts = 0;

    const interval = setInterval(() => {
      const el = document.getElementById(id);

      if (el) {
        clearInterval(interval);
        onFound();
        return;
      }

      attempts++;

      if (attempts >= maxAttempts) {
        clearInterval(interval);
        onNotFound?.();
      }
    }, 150);
  }
}
